import React, { useState } from 'react';
import { useNavigation } from '../contexts/NavigationContext';
import api from '../api'; 
import { 
    FaSignInAlt, FaCheckCircle, FaTimes, FaCommentDots, FaUserGraduate, 
    FaChalkboardTeacher, FaWeixin, FaBookOpen 
} from 'react-icons/fa';

const LandingPage = () => {
    const { navigateTo } = useNavigation();
    
    // --- UI STATES ---
    const [modal, setModal] = useState(null); 
    const [loading, setLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');

    // --- FORM DATA ---
    const [formData, setFormData] = useState({
        name: '', email: '', phone: '', wechat: '', whatsapp: '', facebook: '', instagram: '', message: ''
    });

    const handleSendEnquiry = async (e) => {
        e.preventDefault();
        const contacts = [formData.email, formData.phone, formData.wechat, formData.whatsapp, formData.facebook, formData.instagram];
        const hasContact = contacts.some(c => c?.trim() !== '');

        if (!hasContact) {
            alert("Please provide at least one contact method.");
            return;
        }

        setLoading(true);
        try {
            await api.post('/enquiries', {
                type: modal === 'student-inquiry' ? 'Student Inquiry' : 'Teacher Application',
                name: formData.name || "Guest",
                contactDetails: formData,
                message: formData.message
            });
            setSuccessMsg("Message Sent! We will contact you shortly.");
            setFormData({ name: '', email: '', phone: '', wechat: '', whatsapp: '', facebook: '', instagram: '', message: '' });
        } catch (error) {
            alert("Could not send message. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const closeModal = () => {
        setModal(null);
        setSuccessMsg('');
    };

    return (
        <div className="h-screen w-full bg-gradient-to-br from-cyan-50 via-cyan-100 to-cyan-200 font-sans text-gray-800 flex flex-col items-center justify-center p-2 sm:p-4 overflow-hidden">
            
            {/* --- MAIN CARD --- */}
            <div className="relative z-10 w-full max-w-2xl h-full max-h-[96vh] flex flex-col">
                <div className="bg-white/95 backdrop-blur-md rounded-[2.5rem] shadow-2xl border border-white overflow-hidden flex flex-col h-full">
                    
                    <div className="py-2 sm:py-4 flex justify-center flex-shrink-0 bg-white">
                        <img 
                            src="/images/logo512.png" 
                            alt="BouncePen Logo" 
                            className="h-10 sm:h-14 w-auto object-contain"
                            onError={(e) => { e.target.style.display = 'none'; }} 
                        />
                    </div>

                    <div className="flex-shrink-1 min-h-[100px] w-full bg-gray-100 border-y border-gray-100 overflow-hidden">
                        <img 
                            src="/images/bouncekes.webp" 
                            alt="BouncePen Banner" 
                            className="w-full h-full object-cover"
                        />
                    </div>

                    <div className="flex-grow flex flex-col justify-between p-4 sm:p-8 overflow-hidden">
                        
                        <div className="space-y-2 sm:space-y-3 text-sm sm:text-lg font-bold text-gray-700 mx-auto inline-block">
                            <div className="flex items-center gap-3 sm:gap-4">
                                <FaCheckCircle className="text-green-500 text-lg sm:text-xl flex-shrink-0" /> 
                                <span>Learn English as a Second Language</span>
                            </div>
                            <div className="flex items-center gap-3 sm:gap-4">
                                <FaCheckCircle className="text-green-500 text-lg sm:text-xl flex-shrink-0" /> 
                                <span>Prepare for the IELTS Exam</span>
                            </div>
                            <div className="flex items-center gap-3 sm:gap-4">
                                <FaCheckCircle className="text-green-500 text-lg sm:text-xl flex-shrink-0" /> 
                                <span>Excel at the NSW Selective High School English text</span>
                            </div>
                        </div>

                        {/* --- BUTTONS --- */}
                        <div className="mt-4 flex flex-col gap-3">
                            <button 
                                onClick={() => navigateTo('login')}
                                className="w-full max-w-md mx-auto flex items-center justify-center gap-3 py-4 bg-blue-600 text-white text-xl font-black rounded-2xl shadow-xl hover:bg-blue-700 transition-all border-b-4 border-blue-800"
                            >
                                <FaSignInAlt /> PORTAL LOGIN
                            </button>

                            {/* GUEST ACCESS DOOR */}
                            <button 
                                onClick={() => navigateTo('eslCourses')}
                                className="w-full max-w-md mx-auto flex items-center justify-center gap-3 py-3 bg-emerald-500 text-white text-lg font-bold rounded-2xl shadow-lg hover:bg-emerald-600 transition-all border-b-4 border-emerald-700"
                            >
                                <FaBookOpen /> BROWSE LESSONS (GUEST)
                            </button>
                        </div>

                        <div className="flex items-center gap-4 my-2 sm:my-4 opacity-40">
                            <div className="h-px bg-gray-400 flex-1"></div>
                            <span className="text-[10px] font-bold uppercase tracking-widest whitespace-nowrap">Connect With Us</span>
                            <div className="h-px bg-gray-400 flex-1"></div>
                        </div>

                        <div className="grid grid-cols-3 gap-2 sm:gap-3">
                            <button 
                                onClick={() => setModal('student-inquiry')}
                                className="flex flex-col items-center justify-center gap-1 py-2 sm:py-3 bg-white border-2 border-blue-50 rounded-2xl font-bold text-blue-600 hover:bg-blue-50 transition-all text-[10px] sm:text-[11px] shadow-sm"
                            >
                                <FaUserGraduate className="text-xl sm:text-2xl" /> <span>Inquiry</span>
                            </button>

                            <button 
                                onClick={() => setModal('teacher-inquiry')}
                                className="flex flex-col items-center justify-center gap-1 py-2 sm:py-3 bg-white border-2 border-purple-50 rounded-2xl font-bold text-purple-600 hover:bg-purple-50 transition-all text-[10px] sm:text-[11px] shadow-sm"
                            >
                                <FaChalkboardTeacher className="text-xl sm:text-2xl" /> <span>Teach</span>
                            </button>

                            <button 
                                onClick={() => setModal('wechat')}
                                className="flex flex-col items-center justify-center gap-1 py-2 sm:py-3 bg-[#07C160] text-white rounded-2xl font-bold hover:bg-[#06ad56] transition-all text-[10px] sm:text-[11px] shadow-md"
                            >
                                <FaWeixin className="text-xl sm:text-2xl" /> <span>WeChat</span>
                            </button>
                        </div>
                    </div>

                </div>
            </div>

            {/* --- MODALS --- */}
            {(modal === 'student-inquiry' || modal === 'teacher-inquiry') && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                        <div className={`p-5 flex justify-between items-center text-white ${modal === 'student-inquiry' ? 'bg-blue-600' : 'bg-purple-600'}`}>
                            <h2 className="text-2xl font-black flex items-center gap-2">
                                {modal === 'student-inquiry' ? <><FaUserGraduate/> Student Inquiry</> : <><FaChalkboardTeacher/> Applications</>}
                            </h2>
                            <button onClick={closeModal} className="hover:bg-white/20 p-2 rounded-full transition"><FaTimes/></button>
                        </div>
                        <div className="p-8 overflow-y-auto">
                            {successMsg ? (
                                <div className="text-center py-10">
                                    <FaCheckCircle className="text-6xl text-green-500 mx-auto mb-4 animate-bounce" />
                                    <h3 className="text-2xl font-bold text-gray-800">Sent Successfully!</h3>
                                    <button onClick={closeModal} className="mt-8 bg-gray-100 text-gray-600 font-bold py-2 px-8 rounded-full">Close</button>
                                </div>
                            ) : (
                                <form onSubmit={handleSendEnquiry} className="space-y-4">
                                    <input className="w-full p-3 border-2 border-gray-100 rounded-xl text-sm" placeholder="Full Name" value={formData.name} onChange={e=>setFormData({...formData, name:e.target.value})} required/>
                                    <div className="grid grid-cols-2 gap-3">
                                        <input className="w-full p-3 border-2 border-gray-100 rounded-xl text-sm" placeholder="Email" value={formData.email} onChange={e=>setFormData({...formData, email:e.target.value})}/>
                                        <input className="w-full p-3 border-2 border-gray-100 rounded-xl text-sm" placeholder="Phone" value={formData.phone} onChange={e=>setFormData({...formData, phone:e.target.value})}/>
                                        <input className="w-full p-3 border-2 border-gray-100 rounded-xl text-sm" placeholder="WeChat" value={formData.wechat} onChange={e=>setFormData({...formData, wechat:e.target.value})}/>
                                        <input className="w-full p-3 border-2 border-gray-100 rounded-xl text-sm" placeholder="WhatsApp" value={formData.whatsapp} onChange={e=>setFormData({...formData, whatsapp:e.target.value})}/>
                                    </div>
                                    <textarea className="w-full p-3 border-2 border-gray-100 rounded-xl text-sm h-24 resize-none" placeholder="Message..." value={formData.message} onChange={e=>setFormData({...formData, message:e.target.value})}/>
                                    <button disabled={loading} className={`w-full py-4 rounded-xl font-black text-white shadow-lg ${modal === 'student-inquiry' ? 'bg-blue-600' : 'bg-purple-600'}`}>
                                        {loading ? <FaCommentDots className="animate-pulse mx-auto"/> : "SEND MESSAGE"}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {modal === 'wechat' && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
                    <div className="bg-white rounded-[3rem] p-8 shadow-2xl relative max-w-sm w-full text-center border-8 border-[#07C160]">
                        <button onClick={closeModal} className="absolute -top-4 -right-4 bg-red-500 text-white p-3 rounded-full shadow-lg"><FaTimes size={20} /></button>
                        <h3 className="text-2xl font-black mb-6 text-gray-900">Scan to Connect</h3>
                        <img src="/images/wechat.webp" alt="WeChat QR" className="w-56 h-56 mx-auto object-cover rounded-2xl" />
                    </div>
                </div>
            )}

        </div>
    );
};

export default LandingPage;